import { HOTKEYS, HotkeyScope, type HotkeyRegistration } from './model';

type RegisteredHotkey = HotkeyRegistration & { order: number };

type HotkeyRegistry = {
  register: (registration: HotkeyRegistration) => () => void;
};

const hotkeyScopePriority: Readonly<Record<HotkeyScope, number>> = {
  [HotkeyScope.FIELD]: 1,
  [HotkeyScope.PAGE]: 2,
  [HotkeyScope.OVERLAY]: 3,
} as const;

const isMacPlatform = (): boolean => navigator.platform.toLowerCase().includes('mac');

const isEditableTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  return target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
};

const isCombinationMatched = (event: KeyboardEvent, registration: RegisteredHotkey): boolean => {
  const definition = HOTKEYS[registration.command];
  const isModPressed: boolean = isMacPlatform() ? event.metaKey : event.ctrlKey;
  const isEditableAllowed: boolean = registration.allowInEditable ?? definition.allowInEditable;

  return (
    !event.isComposing &&
    event.code === definition.code &&
    event.shiftKey === definition.isShiftRequired &&
    event.altKey === false &&
    event.metaKey === (definition.isModRequired && isMacPlatform()) &&
    event.ctrlKey === (definition.isModRequired && !isMacPlatform()) &&
    (definition.isModRequired ? isModPressed : !event.metaKey && !event.ctrlKey) &&
    (!isEditableTarget(event.target) || isEditableAllowed)
  );
};

const createHotkeyRegistry = (target: Window): HotkeyRegistry => {
  let registrations: RegisteredHotkey[] = [];
  let nextOrder: number = 0;

  const register = (registration: HotkeyRegistration): (() => void) => {
    const entry: RegisteredHotkey = { ...registration, order: nextOrder };
    nextOrder += 1;
    registrations.push(entry);

    return (): void => {
      registrations = registrations.filter(
        (candidate: RegisteredHotkey): boolean => candidate !== entry,
      );
    };
  };

  target.addEventListener('keydown', (event: KeyboardEvent): void => {
    const matchedRegistration: RegisteredHotkey | undefined = registrations
      .filter(
        (registration: RegisteredHotkey): boolean =>
          (registration.enabled?.() ?? true) && isCombinationMatched(event, registration),
      )
      .sort(
        (left: RegisteredHotkey, right: RegisteredHotkey): number =>
          hotkeyScopePriority[right.scope] - hotkeyScopePriority[left.scope] ||
          right.order - left.order,
      )[0];

    if (!matchedRegistration) {
      return;
    }

    matchedRegistration.handler(event);
    event.preventDefault();
  });

  return { register };
};

export { createHotkeyRegistry, type HotkeyRegistry };
